import { useEffect, useMemo, useRef, useState } from 'react';
import CameraControles from './CameraControles';

type Icone = 'galeria' | 'camera';

function IconeFoto({ tipo }: { tipo: Icone }) {
  if (tipo === 'camera') {
    return (
      <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
        <circle cx="12" cy="13" r="4" />
      </svg>
    );
  }

  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

export default function SeletorFotos({
  arquivos,
  onChange,
  max = 5,
  disabled = false,
}: {
  arquivos: File[];
  onChange: (files: File[]) => void;
  max?: number;
  disabled?: boolean;
}) {
  const galeriaRef = useRef<HTMLInputElement>(null);
  const cameraFallbackRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [aviso, setAviso] = useState('');
  const [cameraAberta, setCameraAberta] = useState(false);
  const [cameraCarregando, setCameraCarregando] = useState(false);
  const [cameraErro, setCameraErro] = useState('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const urls = useMemo(() => arquivos.map((f) => URL.createObjectURL(f)), [arquivos]);

  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);

  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream, cameraAberta]);

  useEffect(() => () => pararCamera(stream), [stream]);

  const cheio = arquivos.length >= max;

  function adicionar(novos: File[]) {
    if (!novos.length) return;
    const restantes = Math.max(max - arquivos.length, 0);
    const aceitos = novos.slice(0, restantes);
    setAviso(novos.length > restantes ? `Limite de ${max} fotos por envio. As fotos extras nao foram adicionadas.` : '');
    onChange([...arquivos, ...aceitos].slice(0, max));
  }

  function remover(indice: number) {
    setAviso('');
    onChange(arquivos.filter((_, idx) => idx !== indice));
  }

  function pararCamera(media: MediaStream | null) {
    media?.getTracks().forEach((track) => track.stop());
  }

  function fecharCamera() {
    pararCamera(stream);
    setStream(null);
    setCameraAberta(false);
    setCameraCarregando(false);
    setCameraErro('');
  }

  async function abrirCamera() {
    if (disabled || cheio) return;
    setAviso('');
    setCameraErro('');

    if (!navigator.mediaDevices?.getUserMedia) {
      cameraFallbackRef.current?.click();
      return;
    }

    setCameraAberta(true);
    setCameraCarregando(true);
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
      });
      setStream(media);
    } catch (e) {
      setCameraErro('Nao foi possivel acessar a camera. Verifique a permissao do navegador.');
    } finally {
      setCameraCarregando(false);
    }
  }

  function capturarFoto() {
    const video = videoRef.current;
    if (!video || cheio) return;

    const largura = video.videoWidth || 1280;
    const altura = video.videoHeight || 720;
    const canvas = document.createElement('canvas');
    canvas.width = largura;
    canvas.height = altura;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, largura, altura);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraErro('Nao foi possivel capturar a foto.');
        return;
      }
      const foto = new File([blob], `foto-os-${Date.now()}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
      adicionar([foto]);
      fecharCamera();
    }, 'image/jpeg', 0.9);
  }

  return (
    <div className="seletor-fotos">
      <input
        ref={galeriaRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          adicionar(Array.from(e.currentTarget.files || []));
          e.currentTarget.value = '';
        }}
      />
      <input
        ref={cameraFallbackRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          adicionar(Array.from(e.currentTarget.files || []));
          e.currentTarget.value = '';
        }}
      />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          className="btn sec sm"
          disabled={disabled || cheio}
          onClick={() => galeriaRef.current?.click()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
        >
          <IconeFoto tipo="galeria" />
          Galeria
        </button>
        <button
          type="button"
          className="btn sec sm"
          disabled={disabled || cheio}
          onClick={abrirCamera}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}
        >
          <IconeFoto tipo="camera" />
          Tirar foto
        </button>
        <span style={{ color: 'var(--texto-suave, #64748b)', fontSize: 13, fontWeight: 700 }}>{arquivos.length}/{max} fotos</span>
      </div>

      {(cheio || aviso) && (
        <p style={{ color: 'var(--texto-suave, #64748b)', fontSize: 12, margin: '4px 0 0' }}>
          {aviso || `Limite de ${max} fotos atingido.`}
        </p>
      )}

      {arquivos.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          {arquivos.map((arquivo, indice) => (
            <div
              key={`${arquivo.name}-${arquivo.lastModified}-${indice}`}
              style={{
                position: 'relative',
                width: 72,
                height: 72,
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid var(--borda, #e2e8f0)',
                background: 'var(--cinza, #f8fafc)',
              }}
            >
              <img src={urls[indice]} alt={`Foto selecionada ${indice + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remover(indice)}
                  title="Remover foto"
                  style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    border: 'none',
                    background: 'rgba(15, 23, 42, 0.72)',
                    color: '#fff',
                    cursor: 'pointer',
                    lineHeight: '20px',
                    padding: 0,
                    fontWeight: 800,
                  }}
                >
                  x
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {cameraAberta && (
        <div
          onClick={fecharCamera}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2500,
            padding: 16,
            background: 'rgba(15, 23, 42, 0.72)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: 560,
              borderRadius: 16,
              background: '#fff',
              boxShadow: '0 30px 80px rgba(15, 23, 42, 0.35)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, borderBottom: '1px solid var(--borda, #e2e8f0)' }}>
              <strong>Camera</strong>
              <button type="button" className="btn sec sm" onClick={fecharCamera}>Fechar</button>
            </div>

            <div style={{ background: '#020617', minHeight: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {cameraCarregando && <span style={{ color: '#fff' }}>Abrindo camera...</span>}
              {!cameraCarregando && cameraErro && (
                <div style={{ color: '#fff', padding: 18, textAlign: 'center' }}>
                  <p style={{ marginTop: 0 }}>{cameraErro}</p>
                  <button type="button" className="btn sm" onClick={() => cameraFallbackRef.current?.click()}>Usar camera do dispositivo</button>
                </div>
              )}
              {!cameraErro && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', maxHeight: '70vh', display: cameraCarregando ? 'none' : 'block', objectFit: 'contain' }}
                />
              )}
              {!cameraErro && !cameraCarregando && <CameraControles stream={stream} />}
            </div>

            <div style={{ padding: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ color: 'var(--texto-suave, #64748b)', fontSize: 13 }}>{Math.max(max - arquivos.length, 0)} foto(s) restante(s)</span>
              <button type="button" className="btn" disabled={cameraCarregando || !!cameraErro || cheio} onClick={capturarFoto}>Capturar foto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
